/**
 * DrumSeparator — split a DRUM track into kick / snare / toms / hihat+cymbals
 * in the browser, with OUR three spectral U-Net models (trained 2026-08-15 on
 * our own GMD × 8-kit render; provenance: docs/ml-licensing-audit-2026-05-31.md).
 *
 * Public surface:
 *   await window.DrumSeparator.split(audioBuffer, {
 *       onProgress: (pct, stage) => {},   // 0..1
 *       slug: 'drum-separator',
 *       signal: AbortSignal,              // optional
 *   })
 *   // → { kick, snare, toms, rest: AudioBuffer, sampleRate }
 *   // `rest` = input − (kick + snare + toms) = hihat + cymbals (+ residue):
 *   // three models, four outputs, nothing lost — the parts sum back exactly.
 *
 * Model contract (fixed by training, do not "tune" here):
 *   STFT n_fft 4096, hop 1024, Hann; magnitude only; input [1, 2, 2048, 512]
 *   (bins 0..2047 of 2049, 512 frames ≈ 11.9 s), output soft mask same shape,
 *   applied to the input magnitude; phase from the input.
 *
 * Why a sibling of stem-separator.js and not an extension of it: that engine
 * is one-model / n_fft 2048 and every tool that separates vocals leans on it;
 * changing its shape contract for three drum models would risk all of them.
 * The runtime choices are copied deliberately: WebGPU first with WASM
 * fallback, graphOptimizationLevel 'basic' (the 'all' level breaks fp16-class
 * graphs on wasm — measured twice), chunked inference with a yield between
 * chunks so the page stays alive.
 */
(function(){
'use strict';

var N_FFT = 4096, HOP = 1024, BINS = 2048, CHUNK = 512;
var STEMS = ['kick', 'snare', 'toms'];

var ORT_WASM_BASE = (function(){
    var m = document.querySelector('meta[name="ort-wasm-base"]');
    return (m && m.content) || '/vendor/onnxruntime/';
})();
var ORT_URL = (function(){
    var m = document.querySelector('meta[name="ort-script-url"]');
    return (m && m.content) || (ORT_WASM_BASE + 'ort.min.js');
})();
function modelUrl(stem){
    var m = document.querySelector('meta[name="drum-model-' + stem + '"]');
    return (m && m.content) || ('/vendor/onnx/drums-' + stem + '-fp16.onnx');
}
// Second source (config/assets.php heavy_external ↔ heavyFallback): intl pages
// load from the HF mirror and fall back to timbrica.com; RU pages the reverse.
function modelFallbackUrl(stem){
    var m = document.querySelector('meta[name="drum-model-' + stem + '-fallback"]');
    var u = m && m.content;
    return (u && u !== modelUrl(stem)) ? u : null;
}

var ortLoad = null, sessions = {}, sessionLoad = {}, backendUsed = null;

function tele(slug, action, payload){
    try { window.__ae && window.__ae('feature_use', Object.assign({ slug: slug || 'drum-separator', action: action }, payload || {})); } catch(_){}
}

function loadOrt(){
    if (window.ort) return Promise.resolve();
    if (ortLoad) return ortLoad;
    ortLoad = new Promise(function(res, rej){
        var s = document.createElement('script');
        s.src = ORT_URL;
        s.onload = function(){
            try {
                ort.env.wasm.wasmPaths = ORT_WASM_BASE;
                ort.env.wasm.numThreads = (typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated)
                    ? Math.min(navigator.hardwareConcurrency || 4, 4) : 1;
            } catch(_){}
            res();
        };
        s.onerror = function(){ ortLoad = null; rej(new Error('ort_load_failed')); };
        document.head.appendChild(s);
    });
    return ortLoad;
}

async function fetchBytes(url, onProgress, signal){
    var r = await fetch(url, { signal: signal });
    if (!r.ok) throw new Error('model_fetch_' + r.status);
    var total = +r.headers.get('content-length') || 0;
    if (!r.body || !total) return new Uint8Array(await r.arrayBuffer());
    var reader = r.body.getReader(), got = 0, parts = [];
    for (;;) {
        var c = await reader.read();
        if (c.done) break;
        parts.push(c.value); got += c.value.length;
        if (onProgress) onProgress(got / total);
    }
    var out = new Uint8Array(got), o = 0;
    parts.forEach(function(p){ out.set(p, o); o += p.length; });
    return out;
}

async function ensureSession(stem, slug, onProgress, signal){
    if (sessions[stem]) return sessions[stem];
    if (sessionLoad[stem]) return sessionLoad[stem];
    sessionLoad[stem] = (async function(){
        await loadOrt();
        var bytes;
        try {
            bytes = await fetchBytes(modelUrl(stem), onProgress, signal);
        } catch (e) {
            var alt = modelFallbackUrl(stem);
            if (!alt || (signal && signal.aborted)) throw e;
            tele(slug, 'drum_model_fallback', { stem: stem, reason: String(e && e.message || e).substring(0, 80) });
            bytes = await fetchBytes(alt, onProgress, signal);
        }
        var hasWebGPU = false;
        if ('gpu' in navigator) { try { hasWebGPU = !!(await navigator.gpu.requestAdapter()); } catch(_){} }
        var providers = hasWebGPU ? ['webgpu', 'wasm'] : ['wasm'];
        var opts = { executionProviders: providers, graphOptimizationLevel: 'basic' };
        var sess;
        try {
            sess = await ort.InferenceSession.create(bytes, opts);
        } catch (e) {
            var msg = String(e && e.message || e);
            if (hasWebGPU && (/webgpu/i.test(msg) || /gpu/i.test(msg))) {
                tele(slug, 'drum_webgpu_fallback_to_wasm', { reason: msg.substring(0, 120) });
                sess = await ort.InferenceSession.create(bytes, { executionProviders: ['wasm'], graphOptimizationLevel: 'basic' });
                backendUsed = 'wasm';
            } else { throw e; }
        }
        if (!backendUsed) backendUsed = hasWebGPU ? 'webgpu' : 'wasm';
        sessions[stem] = sess;
        return sess;
    })();
    try { return await sessionLoad[stem]; }
    catch (e) { sessionLoad[stem] = null; throw e; }
}

// ── STFT (Hann, sqrt-magnitude + unit phase), same shape as stem-separator.js ──
function fftInPlace(re, im){
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
    }
    for (var size = 2; size <= n; size <<= 1) {
        var half = size >> 1, ang = 2 * Math.PI / size, wR = Math.cos(ang), wI = Math.sin(ang);
        for (var s = 0; s < n; s += size) {
            var cR = 1, cI = 0;
            for (var k = 0; k < half; k++) {
                var a = s + k, b = a + half;
                var vR = re[b] * cR - im[b] * cI, vI = re[b] * cI + im[b] * cR;
                re[b] = re[a] - vR; im[b] = im[a] - vI; re[a] += vR; im[a] += vI;
                var nR = cR * wR - cI * wI; cI = cR * wI + cI * wR; cR = nR;
            }
        }
    }
}
function ifftInPlace(re, im){
    var n = re.length;
    for (var i = 0; i < n; i++) im[i] = -im[i];
    fftInPlace(re, im);
    for (var j = 0; j < n; j++) { re[j] /= n; im[j] = -im[j] / n; }
}
var WIN = (function(){ var w = new Float32Array(N_FFT); for (var i = 0; i < N_FFT; i++) w[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / N_FFT)); return w; })();

// center=true like torch.stft: reflect-pad n_fft/2 on both sides
function stft(x){
    var pad = N_FFT >> 1, len = x.length;
    var padded = new Float32Array(len + 2 * pad);
    for (var i = 0; i < pad; i++) padded[i] = x[Math.min(pad - i, len - 1)];
    padded.set(x, pad);
    for (var j = 0; j < pad; j++) padded[pad + len + j] = x[Math.max(len - 2 - j, 0)];
    var frames = Math.floor((padded.length - N_FFT) / HOP) + 1;
    var half = N_FFT >> 1, bins = half + 1;
    var mag = new Float32Array(frames * bins), pRe = new Float32Array(frames * bins), pIm = new Float32Array(frames * bins);
    var re = new Float32Array(N_FFT), im = new Float32Array(N_FFT);
    for (var f = 0; f < frames; f++) {
        var off = f * HOP;
        for (var n = 0; n < N_FFT; n++) { re[n] = padded[off + n] * WIN[n]; im[n] = 0; }
        fftInPlace(re, im);
        var base = f * bins;
        for (var k = 0; k < bins; k++) {
            var m = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
            mag[base + k] = m;
            var inv = 1 / (m + 1e-20);
            pRe[base + k] = re[k] * inv; pIm[base + k] = im[k] * inv;
        }
    }
    return { mag: mag, pRe: pRe, pIm: pIm, frames: frames, bins: bins, len: len };
}
function istft(mag, S){
    var half = N_FFT >> 1, bins = S.bins, pad = half;
    var outLen = S.len + 2 * pad;
    var out = new Float32Array(outLen), wsum = new Float32Array(outLen);
    var re = new Float32Array(N_FFT), im = new Float32Array(N_FFT);
    for (var f = 0; f < S.frames; f++) {
        var base = f * bins, off = f * HOP;
        for (var k = 0; k < bins; k++) { re[k] = mag[base + k] * S.pRe[base + k]; im[k] = mag[base + k] * S.pIm[base + k]; }
        for (var k2 = 1; k2 < half; k2++) { re[N_FFT - k2] = re[k2]; im[N_FFT - k2] = -im[k2]; }
        ifftInPlace(re, im);
        for (var n = 0; n < N_FFT; n++) { out[off + n] += re[n] * WIN[n]; wsum[off + n] += WIN[n] * WIN[n]; }
    }
    var y = new Float32Array(S.len);
    for (var i = 0; i < S.len; i++) { var w = wsum[i + pad]; y[i] = w > 1e-8 ? out[i + pad] / w : 0; }
    return y;
}

function yieldFrame(){ return new Promise(function(r){ setTimeout(r, 0); }); }

// ── Приведение уровня входа ───────────────────────────────────────────────────
// Сеть НЕ масштабно-инвариантна, хотя метрика качества — да. Замер 2026-08-21 на
// 24 фрагментах неподвижного набора (probe_gain.py), боевые веса, вход просто
// умножен на константу — идеальная модель дала бы одинаковые числа:
//
//     громкость   kick  snare   toms  остаток
//       -24 дБ    7.09   9.97   0.18    -2.15   ← остаток ХУЖЕ, чем не разделять
//       -12 дБ   12.38  14.62  12.41     7.13
//       номинал  13.54  15.47  14.02     8.15
//       +12 дБ   13.26  15.18  13.76     7.81
//
// То есть тихая запись теряла до 13.8 дБ, и на нашем стенде это было НЕВИДИМО:
// он собран из рендеров примерно одной громкости. Лечится приведением входа к
// уровню, на котором сеть обучалась: маска считается на приведённых магнитудах и
// применяется к ИСХОДНЫМ, поэтому громкость выхода не меняется.
//
// Опора подобрана замером (probe_refsweep.py): оптимум +3 дБ от медианы набора.
// Кривая плоская — разброс 0.76 дБ на диапазоне опоры ±12 дБ, поэтому выбор
// устойчив, а не подогнан под набор.
var REF_RMS = 0.069;
// Потолок усиления +40 дБ: за этой границей файл — цифровая тишина, и
// «приводить» там нечего, усилится только шум.
var SCALE_MAX = 100;

function inputScale(L, R){
    var sum = 0, n = L.length;
    if (!n) return 1;
    for (var i = 0; i < n; i++) { sum += L[i] * L[i] + R[i] * R[i]; }
    var rms = Math.sqrt(sum / (2 * n));
    if (!(rms > 0)) return 1;                       // тишина: приводить нечего
    // ТОЛЬКО ВВЕРХ — форма подсказана замером, а не вкусом. Вред сосредоточен
    // НИЖЕ опорного уровня (до -13.8 дБ), выше он мал (-0.28 дБ на +12 дБ), зато
    // ослабление громкого входа поднимает утечку в ОТСУТСТВУЮЩУЮ часть с -60 до
    // -42 дБ — badge «почти пусто» гаснет, и в MIDI уезжают фантомные удары
    // (замер probe_emptypart.py, груув без снейра).
    //
    // ⚠️ Следствие, которое надо знать: разделение инвариантно к громкости для
    // входа НА уровне опоры и НИЖЕ. Выше опоры оно уже не меняется, потому что
    // там и менять нечего.
    var k = REF_RMS / rms;
    if (k < 1) return 1;                            // громче опоры — не трогаем
    return k > SCALE_MAX ? SCALE_MAX : k;
}

// Run one model over the whole track: chunk the [2, BINS, frames] magnitude into
// [1,2,BINS,CHUNK] tensors, get masks back, return the MASK per channel (0..255).
// Раньше здесь сразу возвращалась маскированная магнитуда, и стем немедленно
// уходил во временную область. Теперь маски трёх моделей нужны одновременно —
// см. alphaWiener ниже.
async function runModel(sess, magL, magR, S, onChunk, signal, scale){
    var bins = S.bins, frames = S.frames;
    var outL = new Uint8Array(frames * bins), outR = new Uint8Array(frames * bins);
    var inputName = sess.inputNames[0], outputName = sess.outputNames[0];
    var nChunks = Math.ceil(frames / CHUNK);
    for (var c = 0; c < nChunks; c++) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var f0 = c * CHUNK, f1 = Math.min(frames, f0 + CHUNK), w = f1 - f0;
        var inp = new Float32Array(2 * BINS * CHUNK);           // zero-padded tail
        for (var f = 0; f < w; f++) {
            var src = (f0 + f) * bins;
            for (var k = 0; k < BINS; k++) {
                inp[k * CHUNK + f]              = magL[src + k] * scale;
                inp[BINS * CHUNK + k * CHUNK + f] = magR[src + k] * scale;
            }
        }
        var feeds = {}; feeds[inputName] = new ort.Tensor('float32', inp, [1, 2, BINS, CHUNK]);
        var res = await sess.run(feeds);
        var mask = res[outputName].data;
        for (var f2 = 0; f2 < w; f2++) {
            var dst = (f0 + f2) * bins;
            for (var k2 = 0; k2 < BINS; k2++) {
                // Маска 0..1 — храним байтом. Три маски на 4-минутный трек это
                // 127 МБ вместо 509 МБ в float32, а вносимая ошибка 1/255 ≈ 0.004
                // на порядок меньше расхождения fp16-экспорта (~0.05), которое
                // мы уже приняли. Держать все три ОДНОВРЕМЕННО обязательно:
                // конкуренция источников — операция над всеми сразу.
                // +0.5 — потому что присваивание в Uint8Array ОТБРАСЫВАЕТ дробную
                // часть, а не округляет: без этого маски систематически занижены
                // на пол-кванта, и остаток расходился с эталоном на 9%.
                outL[dst + k2] = mask[k2 * CHUNK + f2] * 255 + 0.5;
                outR[dst + k2] = mask[BINS * CHUNK + k2 * CHUNK + f2] * 255 + 0.5;
            }
            // bin 2048 (Nyquist) is not modelled — leave it out of every stem; it
            // stays in `rest` via the subtraction.
        }
        if (onChunk) onChunk((c + 1) / nChunks);
        await yieldFrame();
    }
    return { L: outL, R: outR };
}

// ── Конкуренция источников (α-Wiener) ───────────────────────────────────────
// Три модели предсказывают маски НЕЗАВИСИМО: сумма масок ничем не ограничена, и
// каждая забирает лишнее. Всё забранное сверх меры вычитается из входа и
// сваливается в `rest`, поэтому именно остаток страдал сильнее всех.
//
// Лечение: источники делят спектр. Доля каждого считается от суммы всех, включая
// сам остаток как полноправный источник:
//
//     S_i' = |X| · S_i^α / (Σ_j S_j^α + R^α),   S_i = mask_i·|X|,  R = |X| − Σ S_j
//
// ⚠️ Остаток в знаменателе ОБЯЗАТЕЛЕН. Без него формула верна только когда
// моделей хватает на все инструменты; у нас их три из пяти, и замер на
// прод-конфигурации дал -8.7 дБ бочке и -25 дБ остатку.
//
// Замерено 2026-08-20 на 60 отложенных фрагментах, парно, против нынешнего
// поведения: kick +0.33, snare +0.22, toms +0.57, ОСТАТОК +2.54 дБ.
// Стоимость — ноль лишних прогонов модели, только арифметика.
// Отвергнуты замером: перекрытие кусков (+0.03…0.15 при цене 1.4×), MISI,
// усреднение по сдвигам, повторная нормировка, порог маски.
var ALPHA = 2;

// Нормированная магнитуда ОДНОГО стема. Знаменатель пересчитывается для каждого
// стема, чтобы не держать в памяти ещё один массив на весь трек: арифметика
// дешевле модели, а память на телефоне дороже.
function alphaWiener(masks, mag, stem, n){
    var out = new Float32Array(n), inv255 = 1 / 255;
    for (var i = 0; i < n; i++) {
        var m = mag[i];
        if (m <= 0) continue;
        var den = 0, mine = 0, sum = 0;
        for (var t = 0; t < STEMS.length; t++) {
            var v = m * (masks[STEMS[t]][i] * inv255);
            sum += v;
            var p = v * v;                      // α = 2
            den += p;
            if (STEMS[t] === stem) mine = p;
        }
        var r = m - sum; if (r < 0) r = 0;
        den += r * r;
        out[i] = den > 0 ? m * mine / den : 0;
    }
    return out;
}

function toBuffer(L, R, sr){
    var ctx = window.__sharedAudioCtx || (window.__sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)());
    var b = ctx.createBuffer(2, L.length, sr);
    b.getChannelData(0).set(L); b.getChannelData(1).set(R);
    return b;
}

var MODEL_SR = 44100;   // training rate — bins mean nothing at another rate

async function resampleTo(buffer, sr){
    if (buffer.sampleRate === sr) return buffer;
    var ch = Math.min(2, buffer.numberOfChannels);
    var len = Math.ceil(buffer.duration * sr);
    var off = new OfflineAudioContext(ch, len, sr);
    var src = off.createBufferSource(); src.buffer = buffer; src.connect(off.destination); src.start(0);
    return off.startRendering();
}

async function split(buffer, opts){
    opts = opts || {};
    var slug = opts.slug || 'drum-separator', onProgress = opts.onProgress || function(){}, signal = opts.signal;
    buffer = await resampleTo(buffer, MODEL_SR);
    var sr = buffer.sampleRate;
    var L = buffer.getChannelData(0), R = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : L;
    var t0 = performance.now();

    // Pipelined (2026-08-16, Farid: «пока я работаю с одной — можно скачивать
    // вторую»): models download ONE at a time — the first arrives at full
    // bandwidth ~3× sooner than the old three-parallel fetch — and while stem N
    // separates, model N+1 is already downloading. The STFT (no model needed)
    // overlaps the first download too. Progress is clamped monotonic: two
    // overlapped sources would otherwise wiggle the bar backwards.
    var lastP = 0;
    var prog = function(p, st){ if (p > lastP) lastP = p; onProgress(lastP, st); };
    var chain = ensureSession(STEMS[0], slug, function(p){ prog(0.30 * p / STEMS.length, 'model:' + STEMS[0]); }, signal);
    chain.catch(function(){});          // awaited below — a dead model must not ALSO surface as an unhandled rejection

    // STFT once, shared by all three models (runs while the first model downloads)
    var SL = stft(L), SR = stft(R);
    // масштаб считается по ВСЕМУ файлу, а не по куску: покусочная нормировка
    // качала бы маску вместе с динамикой исполнения
    var inScale = inputScale(L, R);
    prog(0.05, 'stft');

    var parts = {}, maskL = {}, maskR = {};
    for (var s = 0; s < STEMS.length; s++) {
        var stem = STEMS[s];
        var sess = await chain;
        if (s + 1 < STEMS.length) {     // start the NEXT download before this stem computes
            chain = ensureSession(STEMS[s + 1], slug, (function(idx){ return function(p){ prog(0.30 * (idx + p) / STEMS.length, 'model:' + STEMS[idx]); }; })(s + 1), signal);
            chain.catch(function(){});
        }
        var r = await runModel(sess, SL.mag, SR.mag, SL, (function(idx){ return function(p){ prog(0.35 + 0.55 * (idx + p) / STEMS.length, 'separate:' + stem); }; })(s), signal, inScale);
        maskL[stem] = r.L; maskR[stem] = r.R;
    }
    prog(0.90, 'assemble');

    // Конкуренция источников, затем во временную область — по одному стему,
    // чтобы одновременно жил ровно один нормированный спектр.
    var nBins = SL.frames * SL.bins;
    for (var s2 = 0; s2 < STEMS.length; s2++) {
        var st2 = STEMS[s2];
        parts[st2] = {
            L: istft(alphaWiener(maskL, SL.mag, st2, nBins), SL),
            R: istft(alphaWiener(maskR, SR.mag, st2, nBins), SR)
        };
        await yieldFrame();
    }

    // 4) rest = input − sum(parts) (hihat + cymbals + whatever the three did not claim)
    var n = L.length, restL = new Float32Array(n), restR = new Float32Array(n);
    for (var q = 0; q < n; q++) {
        restL[q] = L[q] - parts.kick.L[q] - parts.snare.L[q] - parts.toms.L[q];
        restR[q] = R[q] - parts.kick.R[q] - parts.snare.R[q] - parts.toms.R[q];
    }
    var out = {
        kick: toBuffer(parts.kick.L, parts.kick.R, sr),
        snare: toBuffer(parts.snare.L, parts.snare.R, sr),
        toms: toBuffer(parts.toms.L, parts.toms.R, sr),
        rest: toBuffer(restL, restR, sr),
        sampleRate: sr
    };
    onProgress(1, 'done');
    tele(slug, 'drum_split_done', { sec: Math.round(n / sr), ms: Math.round(performance.now() - t0), ep: backendUsed });
    return out;
}

async function preload(slug, onProgress){
    for (var i = 0; i < STEMS.length; i++) await ensureSession(STEMS[i], slug, onProgress, null);
}

window.DrumSeparator = { split: split, preload: preload, isWarm: function(){ return STEMS.every(function(s){ return !!sessions[s]; }); }, backend: function(){ return backendUsed; }, version: '1.4.0' };
})();
